<template>
  <v-dialog :value="value" persistent max-width="900px">
    <nv-form
      v-if="item != null"
      :errors="formErrors"
      :title="formHeader()"
      :loading="loading"
      data-cy="bulk-upload-form"
      @cancel="onCancel"
      @nv-submit="onSubmit"
    >
      <v-file-input
        v-model="csvFile"
        accept=".csv"
        label="Upload CSV file"
        @change="handleFileUpload"
      ></v-file-input>
      <template v-if="channelType === 'parent'">
        <label class="text-subtitle2 font-weight-medium mb-3 d-block">Select Rule Type</label>
        <v-row>
          <v-radio-group v-model="selected_rule_type" row>
            <v-col cols="4">
              <v-radio label="Domain" value="Domain" />
            </v-col>
            <v-col cols="4">
              <v-radio label="OTTM" value="OTTM" />
            </v-col>
            <v-col cols="4">
              <v-radio label="Video" value="Video" />
            </v-col>
          </v-radio-group>
        </v-row>
      </template>
      <v-text-field v-model="fileName" label="File name" readonly></v-text-field>
      <owl-date-picker
        v-if="channelType !== 'parent'"
        v-model="effective_start_date"
        label="Start Date"
        clearable
      />
    </nv-form>
  </v-dialog>
</template>

<script>
import { FormMixin } from '@nielsen-media/ui-toolkit-vue-api';
import OwlDatePicker from '@/components/OwlDatePicker.vue';
import HTTP from '@/api/ant.js';
import { parsePatternsFromCsvBase64 } from '@/helpers/csvPatternParser';
import { sendDomainShadowEvaluations } from '@/helpers/domainShadowApi';

export default {
  name: 'BulkDomainForm',
  components: { OwlDatePicker },
  mixins: [FormMixin],
  props: {
    treeId: {
      type: Number,
      required: true,
    },
    localization: {
      type: String,
      required: true,
    },
    ruleType: {
      type: String,
      required: true,
    },
    channelType: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    csvFile: null,
    fileName: '',
    encodedFileString: null,
    effective_start_date: null,
    selected_rule_type: null,
  }),
  methods: {
    formInit() {
      return { tree_id: this.treeId };
    },
    formHeader() {
      if (!this.ruleType === false) {
        return 'Upload file';
      } else {
        return `Upload ${this.ruleType === 'ottm' ? 'connected devices' : this.ruleType} rules`;
      }
    },
    resetForm() {
      this.csvFile = null;
      this.fileName = '';
      this.encodedFileString = null;
      this.effective_start_date = null;
      this.selected_rule_type = null;
    },
    formSave() {
      this.loading = true;
      if (this.channelType === 'parent') {
        this.item = {
          import_csv: this.encodedFileString,
          rule_type: this.selected_rule_type,
          target_parent_id: this.treeId,
          target_parent_localization: this.localization,
        };
      } else {
        this.item = {
          file_content: this.encodedFileString,
          proposal_type: this.ruleType,
          tree_id: this.treeId,
          localization: this.localization,
          // s3_upload: false,
          effective_start_date: this.effective_start_date,
        };

        if (this.ruleType === 'domain') {
          const patterns = parsePatternsFromCsvBase64(this.encodedFileString);
          if (patterns.length) {
            sendDomainShadowEvaluations(patterns, this.treeId, {
              source: 'bulk-csv-domain-form-save',
              userEmail: this.$auth?.user?.email,
            });
          }
        }
      }

      // Show the snackbar message
      this.$flash(
        'success',
        'The upload is being processed, please navigate to Bulk Upload Reviews page for status',
      );

      // Close the dialog without resetting form data first
      this.$emit('input', false);

      const result =
        this.channelType === 'parent'
          ? this.submitForm(HTTP, 'bulk_localized_rule_import', {})
          : this.submitForm(HTTP, '/bulk_upload/validate_csv', {});

      this.onCancel();

      return result;
    },

    async handleFileUpload(file) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const base64String = btoa(
          Array.from(uint8Array)
            .map(byte => String.fromCharCode(byte))
            .join(''),
        );
        this.encodedFileString = base64String;
        this.fileName = file.name;
        this.csvFile = file;
        return true;
      } catch (error) {
        this.$flash('error', 'Failed to encode file');
        return false;
      }
    },

    // Add validation method
    validateBeforeSubmit() {
      if (!this.csvFile) {
        this.$flash('error', 'Please select a file');
        return false;
      }
      if (!this.encodedFileString) {
        this.$flash('error', 'File encoding failed');
        return false;
      }
      if (this.channelType !== 'parent' && !this.effective_start_date) {
        this.$flash('error', 'Please select a start date');
        return false;
      }
      if (this.channelType === 'parent' && !this.selected_rule_type) {
        this.$flash('error', 'Please select Rule type option');
        return false;
      }
      return true;
    },

    // Modified submit handler
    onSubmit() {
      if (this.validateBeforeSubmit()) {
        this.formSave();
      }
    },
    onCancel() {
      this.loading = false;
      this.resetForm();
      this.formCancel();
    },
  },
};
</script>
